import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useLocation, Link } from "react-router-dom";
import {
  ExternalLink,
  ChevronDown,
  Wallet,
  LogOut,
  Copy,
  Check,
  Sun,
  Moon,
  Monitor,
  Ghost,
  Menu,
  X,
} from "lucide-react";
import { useAppKit } from "@reown/appkit/react";
import { useDisconnect, useBalance } from "wagmi";
import { formatUnits } from "viem";
import { useConnection } from "@/hooks/useConnection";
import { network } from "@/config/walletConnect";
import { createExplorerAddressLink } from "@/utils/explorer";
import logo from "@/assets/images/logo.svg";
import avatar from "@/assets/images/Avatar.png";
import { useAuth } from "@/hooks/useAuth";
import { showToast } from "@/lib/toast";
import { ENV, ENABLE_POINTS_REFERRAL } from "@/config/constants";
import { STORAGE_KEYS } from "@/lib/storageKeys";

export function Header() {
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [themeMode, setThemeMode] = useState<"light" | "dark" | "system">("system");
  const [copied, setCopied] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileWalletOpen, setMobileWalletOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  // Reown/Wagmi hooks
  const { open } = useAppKit();
  const { address, isConnected, isConnecting, isGhostMode, detachGhost } = useConnection();
  const disconnect = useDisconnect();
  const { logout } = useAuth();
  const { data: balance } = useBalance({ address, chainId: network.id });
  const formattedBalance = balance
    ? `${parseFloat(formatUnits(balance.value, balance.decimals)).toFixed(8)} ${balance.symbol}`
    : undefined;

  // Close mobile menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileWalletOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      setHasScrolled(window.scrollY > 0);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const applyThemeMode = (mode: "light" | "dark" | "system") => {
    const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const shouldBeDark = mode === "system" ? systemPrefersDark : mode === "dark";

    setThemeMode(mode);
    setIsDarkMode(shouldBeDark);

    document.documentElement.classList.remove("light", "dark");
    if (!shouldBeDark) {
      document.documentElement.classList.add("light");
    }
  };

  // Initialize theme from localStorage or default to system
  useEffect(() => {
    const savedTheme = localStorage.getItem(STORAGE_KEYS.THEME) as
      | "light"
      | "dark"
      | "system"
      | null;
    if (!savedTheme) {
      localStorage.setItem(STORAGE_KEYS.THEME, "system");
    }
    applyThemeMode(savedTheme ?? "system");
  }, []);

  useEffect(() => {
    if (themeMode !== "system") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => applyThemeMode("system");
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [themeMode]);

  const selectTheme = (mode: "light" | "dark" | "system") => {
    localStorage.setItem(STORAGE_KEYS.THEME, mode);
    applyThemeMode(mode);
  };

  const isActiveRoute = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleConnect = async () => {
    await open();
  };

  const handleDisconnect = () => {
    logout();
    disconnect.mutate();
  };

  const handleCopyAddress = async () => {
    if (address) {
      try {
        await navigator.clipboard.writeText(address);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        showToast.success({
          title: "Address copied",
          message: "Wallet address copied to clipboard.",
          duration: 2000,
        });
      } catch {
        showToast.error({
          title: "Copy failed",
          message: "Unable to copy wallet address to clipboard.",
          duration: 3000,
        });
      }
    }
  };

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const navLinks = [
    { to: "/", label: "Swap" },
    { to: "/pools", label: "Pools" },
    { to: "/portfolio", label: "Portfolio" },
  ];

  // Always rendered inside the "More" dropdown (never inline) so the primary
  // nav stays focused on the swap/pools/stake/portfolio loop. Bridge lives
  // here too — it's a less-frequent action than the main four.
  const overflowNavLinks = [
    { to: "/bridge", label: "Bridge" },
    ...(ENABLE_POINTS_REFERRAL
      ? [
          { to: "/points", label: "Points" },
          { to: "/referral", label: "Referral" },
        ]
      : []),
  ];

  const explorerUrl = address ? createExplorerAddressLink(address) : undefined;

  // Shared wallet menu items
  const walletMenuItems = (
    <>
      <button
        onClick={handleCopyAddress}
        className="flex items-center w-full px-6 py-3 text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      >
        {copied ? (
          <Check className="mr-3 h-4 w-4" />
        ) : (
          <Copy className="mr-3 h-4 w-4" />
        )}
        {copied ? "Copied!" : "Copy Address"}
      </button>
      {explorerUrl && (
        <a
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center w-full px-6 py-3 text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <ExternalLink className="mr-3 h-4 w-4" />
          View in Explorer
        </a>
      )}
      {isGhostMode && (
        <button
          onClick={detachGhost}
          className="flex items-center w-full px-6 py-3 text-base font-medium text-destructive hover:bg-muted transition-colors"
        >
          <Ghost className="mr-3 h-4 w-4" />
          Detach Ghost
        </button>
      )}
      {!isGhostMode && (
        <button
          onClick={handleDisconnect}
          className="flex items-center w-full px-6 py-3 text-base font-medium text-destructive hover:bg-muted transition-colors"
        >
          <LogOut className="mr-3 h-4 w-4" />
          Disconnect
        </button>
      )}
    </>
  );

  return (
    <>
    <Sheet
      open={mobileMenuOpen}
      onOpenChange={(open) => {
        setMobileMenuOpen(open);
        if (open) setMobileWalletOpen(false);
      }}
    >
      <SheetContent
        className="lg:hidden flex flex-col px-8"
        style={{
          paddingTop: "max(env(safe-area-inset-top), 1.75rem)",
          paddingBottom: "max(env(safe-area-inset-bottom), 1.5rem)",
        }}
      >
        <SheetTitle className="sr-only">Navigation menu</SheetTitle>
        <Link
          to="/"
          aria-label="Go to Swap"
          className="mb-12 flex w-fit items-center"
          onClick={() => setMobileMenuOpen(false)}
        >
          <img
            src={logo}
            alt="Helm"
            className={`h-8 w-auto ${!isDarkMode ? "invert" : ""}`}
          />
        </Link>

        <nav className="flex flex-col gap-1">
          {[...navLinks, ...overflowNavLinks].map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setMobileMenuOpen(false)}
              className={`py-2 text-2xl font-medium leading-tight transition-colors hover:text-foreground ${
                isActiveRoute(to) ? "text-primary" : "text-foreground"
              }`}
            >
              {label}
            </Link>
          ))}
        </nav>

        <nav className="mt-12 flex flex-col gap-1 border-t pt-8">
          <a
            href="https://docs.helmfinance.xyz"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileMenuOpen(false)}
            className="py-2 text-lg font-medium text-foreground transition-colors hover:text-primary"
          >
            Docs
          </a>
          <a
            href="https://x.com/helmfinanceX"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileMenuOpen(false)}
            className="py-2 text-lg font-medium text-foreground transition-colors hover:text-primary"
          >
            X (formerly Twitter)
          </a>
        </nav>
      </SheetContent>
    </Sheet>

    {/* Mobile wallet backdrop stays outside the header stacking context. */}
    {mobileWalletOpen && (
      <div
        className="lg:hidden fixed inset-0 bg-black/80 backdrop-blur-md z-40"
        aria-hidden="true"
        onClick={() => {
          setMobileWalletOpen(false);
        }}
      />
    )}
    <header
      className={`fixed inset-x-0 z-100 flex w-full items-start justify-start border-0 ${
        hasScrolled
          ? "bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60"
          : "bg-transparent backdrop-blur-0 supports-backdrop-filter:bg-transparent"
      } ${
        ENV !== "mainnet" && !hasScrolled ? "top-7" : "top-0"
      } ${hasScrolled ? "animate-[slideInFromTop_220ms_ease-out]" : ""}`}
    >
      <div className="w-full flex h-16 items-center justify-between px-4 md:px-4">
        <div className="flex items-center space-x-3 lg:space-x-12">
          {/* Hamburger button — mobile only */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setMobileMenuOpen((v) => !v);
              setMobileWalletOpen(false);
            }}
            className="lg:hidden h-9 w-9 p-0 border border-border shadow-none"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-4 w-4" />
            ) : (
              <Menu className="h-4 w-4" />
            )}
          </Button>

          <Link to="/" aria-label="Go to Swap" className="flex items-center flex-shrink-0">
            <img
              src={logo}
              alt="Helm"
              className={`h-6 lg:h-8 w-auto cursor-pointer ${!isDarkMode ? "invert" : ""}`}
            />
          </Link>

          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`text-base font-medium transition-colors hover:text-primary ${
                  isActiveRoute(to) ? "text-primary" : "text-muted-foreground"
                }`}
              >
                {label}
              </Link>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`flex items-center space-x-1 text-base font-medium transition-colors hover:text-primary ${
                  overflowNavLinks.some(({ to }) => isActiveRoute(to))
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              >
                <span>More</span>
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {overflowNavLinks.map(({ to, label }) => (
                  <DropdownMenuItem key={to} asChild>
                    <Link
                      to={to}
                      className={`flex items-center cursor-pointer ${
                        isActiveRoute(to) ? "text-primary" : ""
                      }`}
                    >
                      {label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild>
                  <a
                    href="https://docs.helmfinance.xyz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between cursor-pointer"
                  >
                    Docs
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a
                    href="https://x.com/helmfinanceX"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between cursor-pointer"
                  >
                    X (formerly Twitter)
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>

        <div className="flex w-fit items-end justify-center space-x-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 border border-border shadow-none rounded-[6px]"
                aria-label="Select theme"
              >
                {themeMode === "system" ? (
                  <Monitor className="h-4 w-4" />
                ) : isDarkMode ? (
                  <Moon className="h-4 w-4" />
                ) : (
                  <Sun className="h-4 w-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              <DropdownMenuItem onClick={() => selectTheme("dark")} className="cursor-pointer">
                <Moon className="mr-2 h-4 w-4" />
                Dark
                {themeMode === "dark" && <Check className="ml-auto h-4 w-4" />}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => selectTheme("light")} className="cursor-pointer">
                <Sun className="mr-2 h-4 w-4" />
                Light
                {themeMode === "light" && <Check className="ml-auto h-4 w-4" />}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => selectTheme("system")} className="cursor-pointer">
                <Monitor className="mr-2 h-4 w-4" />
                System
                {themeMode === "system" && <Check className="ml-auto h-4 w-4" />}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {!isConnected ? (
            <Button
              onClick={handleConnect}
              disabled={isConnecting}
              variant="default"
              size="default"
              className="flex items-center gap-2 shadow-none rounded-[6px]"
            >
              <Wallet className="h-4 w-4" />
              <span className="hidden lg:inline">
                {isConnecting ? "Connecting..." : "Connect Wallet"}
              </span>
            </Button>
          ) : (
            <>
              {/* Desktop: radix dropdown */}
              <div className="mr-0 hidden lg:block">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="default"
                      className="flex h-9 items-center gap-2 px-2 py-2 shadow-none bg-transparent hover:bg-transparent"
                    >
                      {isGhostMode ? (
                        <Ghost className="h-5 w-5 text-purple-500" />
                      ) : (
                        <img src={avatar} alt="" className="h-5 w-5 rounded-full" />
                      )}
                      <span>{address && formatAddress(address)}</span>
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2 border-b flex items-center gap-2">
                      <img src={avatar} alt="" className="h-7 w-7 rounded-full flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium">{address && formatAddress(address)}</div>
                        {formattedBalance && (
                          <div className="text-xs text-muted-foreground mt-0.5">{formattedBalance}</div>
                        )}
                      </div>
                    </div>
                    <DropdownMenuItem onClick={handleCopyAddress} className="cursor-pointer">
                      {copied ? (
                        <Check className="mr-2 h-4 w-4" />
                      ) : (
                        <Copy className="mr-2 h-4 w-4" />
                      )}
                      {copied ? "Copied!" : "Copy Address"}
                    </DropdownMenuItem>
                    {explorerUrl && (
                      <DropdownMenuItem asChild>
                        <a
                          href={explorerUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center cursor-pointer"
                        >
                          <ExternalLink className="mr-2 h-4 w-4" />
                          View in Explorer
                        </a>
                      </DropdownMenuItem>
                    )}
                    {isGhostMode && (
                      <DropdownMenuItem
                        onClick={detachGhost}
                        className="cursor-pointer text-destructive focus:text-destructive"
                      >
                        <Ghost className="mr-2 h-4 w-4" />
                        Detach Ghost
                      </DropdownMenuItem>
                    )}
                    {!isGhostMode && (
                      <DropdownMenuItem
                        onClick={handleDisconnect}
                        className="cursor-pointer text-destructive focus:text-destructive"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Disconnect
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Mobile: toggle overlay */}
              <Button
                variant="outline"
                size="default"
                onClick={() => {
                  setMobileWalletOpen((v) => !v);
                  setMobileMenuOpen(false);
                }}
                className="lg:hidden flex items-center gap-2 shadow-none h-9 px-2 bg-transparent"
              >
                {isGhostMode ? (
                  <Ghost className="h-5 w-5 text-purple-500" />
                ) : (
                  <img src={avatar} alt="" className="h-5 w-5 rounded-full" />
                )}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Mobile wallet menu overlay */}
      {mobileWalletOpen && (
        <div className="lg:hidden absolute top-16 left-0 right-0 border-t border-b bg-background z-50 shadow-md">
          <div className="px-6 py-3 border-b flex items-center gap-3">
            <img src={avatar} alt="" className="h-8 w-8 rounded-full flex-shrink-0" />
            <div>
              <div className="text-sm font-medium">{address && formatAddress(address)}</div>
              {formattedBalance && (
                <div className="text-xs text-muted-foreground mt-0.5">{formattedBalance}</div>
              )}
            </div>
          </div>
          <div className="flex flex-col py-2">
            {walletMenuItems}
          </div>
        </div>
      )}
    </header>
    </>
  );
}
